/**
 * HR Authority Dashboard Page
 * Phase P1: Read-only transparency page showing RBAC/RACI authority levels
 * 
 * Visible only to: SUPER_ADMIN + all HR roles
 * 4 Tabs: Roles Overview, Permission Matrix, RACI Processes, My Permissions
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Search, 
  Users, 
  Shield, 
  GitBranch, 
  User,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Zap,
  Eye,
  Lock,
  Filter
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Color coding legend for roles
const ROLE_COLOR_MAP = {
  // Standard access - Green
  employee: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', category: 'standard' },
  manager: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', category: 'standard' },
  
  // Approval authority - Amber
  hr_assistant: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_ops: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_admin: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_er: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_gr: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_ld: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_pc: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_ta: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  hr_sys: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', category: 'approval' },
  
  // Read-only/Compliance - Blue
  hr_aud: { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-300', category: 'readonly' },
  
  // System authority - Cyan (2026-06: separated from HR)
  it_inf: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300', category: 'system' },
  
  // Bypass/Super authority - Purple
  hr_manager: { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300', category: 'bypass' },
  super_admin: { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300', category: 'bypass' },
};

// RACI badge colors
const RACI_COLORS = {
  R: { bg: 'bg-blue-500', text: 'text-white', label: 'Responsible' },
  A: { bg: 'bg-purple-600', text: 'text-white', label: 'Accountable' },
  C: { bg: 'bg-amber-500', text: 'text-white', label: 'Consulted' },
  I: { bg: 'bg-slate-400', text: 'text-white', label: 'Informed' },
};

// Permission modules for grouping
const PERMISSION_MODULES = [
  'employee', 'leave', 'request', 'letters', 'document', 'user', 
  'audit', 'system', 'payroll', 'disciplinary', 'govt', 'training', 
  'recruitment', 'reports', 'company', 'dashboard', 'hr', 'super'
];

export default function AuthorityPage() {
  const { token } = useAuth();
  const { isRTL } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [raci, setRaci] = useState([]);
  const [myPermissions, setMyPermissions] = useState(null);
  
  // Filter states
  const [roleSearch, setRoleSearch] = useState('');
  const [permissionSearch, setPermissionSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [raciSearch, setRaciSearch] = useState('');
  const [myPermSearch, setMyPermSearch] = useState('');
  
  // Compare Roles states (P2)
  const [compareRoleA, setCompareRoleA] = useState('');
  const [compareRoleB, setCompareRoleB] = useState('');
  const [compareSearch, setCompareSearch] = useState('');
  const [compareModuleFilter, setCompareModuleFilter] = useState('all');
  const [showDiffsOnly, setShowDiffsOnly] = useState(true);
  // Fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [rolesRes, permsRes, matrixRes, raciRes, myPermsRes] = await Promise.all([
          axios.get(`${API_URL}/rbac/roles`, { headers }),
          axios.get(`${API_URL}/rbac/permissions`, { headers }),
          axios.get(`${API_URL}/rbac/matrix`, { headers }),
          axios.get(`${API_URL}/rbac/raci`, { headers }),
          axios.get(`${API_URL}/rbac/me/permissions`, { headers }),
        ]);
        
        setRoles(rolesRes.data || []);
        setPermissions(permsRes.data || []);
        setMatrix(matrixRes.data || null);
        setRaci(raciRes.data || []);
        setMyPermissions(myPermsRes.data || null);
      } catch (err) {
        console.error('Failed to fetch authority data:', err);
        // Handle error response - detail can be string or object {error, code, required_permission}
        const detail = err.response?.data?.detail;
        if (typeof detail === 'string') {
          setError(detail);
        } else if (detail?.error) {
          setError(detail.error);
        } else {
          setError('Failed to load authority data');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchData();
    }
  }, [token]);

  // Filtered roles
  const filteredRoles = useMemo(() => {
    if (!roleSearch) return roles;
    const search = roleSearch.toLowerCase();
    return roles.filter(r => 
      r.display_name.toLowerCase().includes(search) ||
      r.value.toLowerCase().includes(search) ||
      r.description.toLowerCase().includes(search)
    );
  }, [roles, roleSearch]);

  // Filtered permissions for matrix
  const filteredPermissions = useMemo(() => {
    if (!permissions) return [];
    let filtered = permissions;
    
    if (moduleFilter && moduleFilter !== 'all') {
      filtered = filtered.filter(p => p.module.toLowerCase() === moduleFilter.toLowerCase());
    }
    
    if (permissionSearch) {
      const search = permissionSearch.toLowerCase();
      filtered = filtered.filter(p => 
        p.value.toLowerCase().includes(search) ||
        p.module.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [permissions, moduleFilter, permissionSearch]);

  // Grouped permissions by module for my permissions tab
  const groupedMyPermissions = useMemo(() => {
    if (!myPermissions?.permissions) return {};
    
    const groups = {};
    let filtered = myPermissions.permissions;
    
    if (myPermSearch) {
      const search = myPermSearch.toLowerCase();
      filtered = filtered.filter(p => p.toLowerCase().includes(search));
    }
    
    filtered.forEach(perm => {
      const parts = perm.split('_');
      const module = parts[0] || 'other';
      if (!groups[module]) groups[module] = [];
      groups[module].push(perm);
    });
    
    return groups;
  }, [myPermissions, myPermSearch]);

  // Filtered RACI processes
  const filteredRaci = useMemo(() => {
    if (!raciSearch) return raci;
    const search = raciSearch.toLowerCase();
    return raci.filter(r => 
      r.process_name_en.toLowerCase().includes(search) ||
      r.process_code.toLowerCase().includes(search)
    );
  }, [raci, raciSearch]);

  // Role Comparison computation (P2) - uses cached matrix data
  const roleComparison = useMemo(() => {
    if (!matrix || !compareRoleA || !compareRoleB || compareRoleA === compareRoleB) {
      return null;
    }
    
    const permsA = new Set(matrix.matrix[compareRoleA] || []);
    const permsB = new Set(matrix.matrix[compareRoleB] || []);
    
    // Compute differences
    const onlyInA = [...permsA].filter(p => !permsB.has(p));
    const onlyInB = [...permsB].filter(p => !permsA.has(p));
    const shared = [...permsA].filter(p => permsB.has(p));
    
    // Apply filters
    const filterPerm = (perm) => {
      // Module filter
      if (compareModuleFilter && compareModuleFilter !== 'all') {
        const parts = perm.split('_');
        const module = parts[0]?.toLowerCase();
        if (module !== compareModuleFilter.toLowerCase()) return false;
      }
      // Search filter
      if (compareSearch) {
        if (!perm.toLowerCase().includes(compareSearch.toLowerCase())) return false;
      }
      return true;
    };
    
    const filteredOnlyA = onlyInA.filter(filterPerm).sort();
    const filteredOnlyB = onlyInB.filter(filterPerm).sort();
    const filteredShared = showDiffsOnly ? [] : shared.filter(filterPerm).sort();
    
    return {
      roleAName: roles.find(r => r.value === compareRoleA)?.display_name || compareRoleA,
      roleBName: roles.find(r => r.value === compareRoleB)?.display_name || compareRoleB,
      onlyInA: filteredOnlyA,
      onlyInB: filteredOnlyB,
      shared: filteredShared,
      counts: {
        onlyA: onlyInA.length,
        onlyB: onlyInB.length,
        shared: shared.length,
        filteredOnlyA: filteredOnlyA.length,
        filteredOnlyB: filteredOnlyB.length,
        filteredShared: filteredShared.length,
      }
    };
  }, [matrix, compareRoleA, compareRoleB, compareSearch, compareModuleFilter, showDiffsOnly, roles]);

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="authority-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="authority-error">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={cn("p-6 space-y-6", isRTL && "rtl")} data-testid="authority-page">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="authority-title">
          {isRTL ? 'صلاحيات الموارد البشرية' : 'HR Authority'}
        </h1>
        <p className="text-slate-600">
          {isRTL 
            ? 'عرض نموذج الأدوار والصلاحيات ومصفوفة RACI'
            : 'View the RBAC roles, permissions, and RACI governance model'}
        </p>
      </div>

      {/* Color Legend */}
      <Card className="bg-slate-50 border-slate-200">
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium text-slate-700">
            {isRTL ? 'دليل الألوان' : 'Color Legend'}
          </CardTitle>
        </CardHeader>
        <CardContent className="py-2">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-500" />
              <span className="text-sm text-slate-600">{isRTL ? 'وصول عادي' : 'Standard Access'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-500" />
              <span className="text-sm text-slate-600">{isRTL ? 'صلاحية الموافقة' : 'Approval Authority'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-rose-500" />
              <span className="text-sm text-slate-600">{isRTL ? 'حذف/تطهير' : 'Destructive (Delete/Purge)'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-violet-500" />
              <span className="text-sm text-slate-600">{isRTL ? 'تجاوز/صلاحية عليا' : 'Bypass/Super Authority'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-sky-500" />
              <span className="text-sm text-slate-600">{isRTL ? 'قراءة فقط/امتثال' : 'Read-only/Compliance'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="roles" data-testid="tab-roles" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'الأدوار' : 'Roles'}</span>
          </TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'المصفوفة' : 'Matrix'}</span>
          </TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare" className="flex items-center gap-2">
            <GitBranch className="h-4 w-4 rotate-90" />
            <span className="hidden sm:inline">{isRTL ? 'مقارنة' : 'Compare'}</span>
          </TabsTrigger>
          <TabsTrigger value="raci" data-testid="tab-raci" className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" />
            <span className="hidden sm:inline">RACI</span>
          </TabsTrigger>
          <TabsTrigger value="my-permissions" data-testid="tab-my-perms" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'صلاحياتي' : 'My Perms'}</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab A: Roles Overview */}
        <TabsContent value="roles" data-testid="roles-tab-content">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={isRTL ? 'بحث في الأدوار...' : 'Search roles...'}
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                  className="pl-10"
                  data-testid="roles-search"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRoles.map(role => {
                const colors = ROLE_COLOR_MAP[role.value] || { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-300' };
                return (
                  <Card 
                    key={role.value} 
                    className={cn("border-2 transition-all hover:shadow-md", colors.border)}
                    data-testid={`role-card-${role.value}`}
                  >
                    <CardHeader className={cn("pb-2", colors.bg)}>
                      <CardTitle className={cn("text-lg font-semibold", colors.text)}>
                        {role.display_name}
                      </CardTitle>
                      <CardDescription className="text-xs text-slate-600">
                        {role.value}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-3 space-y-3">
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {role.description || 'No description available'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {role.can_bypass && (
                          <Badge variant="secondary" className="bg-violet-100 text-violet-700 text-xs">
                            <Zap className="h-3 w-3 mr-1" />
                            {isRTL ? 'تجاوز' : 'Bypass'}
                          </Badge>
                        )}
                        {role.is_hr_role && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700 text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            HR
                          </Badge>
                        )}
                        {role.value === 'hr_aud' && (
                          <Badge variant="secondary" className="bg-sky-100 text-sky-700 text-xs">
                            <Eye className="h-3 w-3 mr-1" />
                            {isRTL ? 'قراءة فقط' : 'Read-only'}
                          </Badge>
                        )}
                      </div>
                      <div className="pt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-500">
                          {isRTL ? 'الصلاحيات:' : 'Permissions:'} {role.permission_count}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Tab B: Permission Matrix */}
        <TabsContent value="matrix" data-testid="matrix-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'مصفوفة الصلاحيات' : 'Permission Matrix'}</CardTitle>
              <CardDescription>
                {isRTL ? 'الصلاحيات حسب الأدوار' : 'Permissions by role'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder={isRTL ? 'بحث في الصلاحيات...' : 'Search permissions...'}
                      value={permissionSearch}
                      onChange={(e) => setPermissionSearch(e.target.value)}
                      className="pl-10"
                      data-testid="matrix-search"
                    />
                  </div>
                  <Select value={moduleFilter} onValueChange={setModuleFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="matrix-module-filter">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by module" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{isRTL ? 'جميع الوحدات' : 'All Modules'}</SelectItem>
                      {PERMISSION_MODULES.map(mod => (
                        <SelectItem key={mod} value={mod}>{mod.charAt(0).toUpperCase() + mod.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Matrix Table */}
                <div className="overflow-auto max-h-[600px] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0 z-10">
                      <tr>
                        <th className="sticky left-0 z-20 bg-slate-100 px-4 py-3 text-left font-semibold text-slate-700 min-w-[200px] border-r">
                          {isRTL ? 'الصلاحية' : 'Permission'}
                        </th>
                        {matrix?.roles?.map(role => (
                          <th key={role.value} className="px-2 py-3 text-center font-medium text-slate-600 min-w-[80px]">
                            <span className="text-xs">{role.display_name.split(' ')[0]}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredPermissions.map((perm, idx) => (
                        <tr key={perm.value} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="sticky left-0 z-10 px-4 py-2 font-mono text-xs bg-inherit border-r">
                            <span className="text-slate-600">{perm.module}/</span>
                            <span className="text-slate-900">{perm.action}</span>
                          </td>
                          {matrix?.roles?.map(role => {
                            const hasPermission = matrix.matrix[role.value]?.includes(perm.value);
                            return (
                              <td key={role.value} className="px-2 py-2 text-center">
                                {hasPermission ? (
                                  <CheckCircle2 className="h-4 w-4 mx-auto text-emerald-500" />
                                ) : (
                                  <XCircle className="h-4 w-4 mx-auto text-slate-300" />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500">
                  {isRTL 
                    ? `عرض ${filteredPermissions.length} من ${permissions.length} صلاحية`
                    : `Showing ${filteredPermissions.length} of ${permissions.length} permissions`}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Compare Roles (P2) */}
        <TabsContent value="compare" data-testid="compare-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'مقارنة الأدوار' : 'Compare Roles'}</CardTitle>
              <CardDescription>
                {isRTL 
                  ? 'مقارنة الصلاحيات بين دورين لرؤية الاختلافات'
                  : 'Compare permissions between two roles to see differences'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Role Selectors */}
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-2 min-w-[200px]">
                    <label className="text-sm font-medium text-slate-700">
                      {isRTL ? 'الدور أ' : 'Role A'}
                    </label>
                    <Select value={compareRoleA} onValueChange={setCompareRoleA}>
                      <SelectTrigger data-testid="compare-role-a">
                        <SelectValue placeholder={isRTL ? 'اختر الدور...' : 'Select role...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="text-slate-400 font-bold text-lg pb-2">vs</div>
                  
                  <div className="space-y-2 min-w-[200px]">
                    <label className="text-sm font-medium text-slate-700">
                      {isRTL ? 'الدور ب' : 'Role B'}
                    </label>
                    <Select value={compareRoleB} onValueChange={setCompareRoleB}>
                      <SelectTrigger data-testid="compare-role-b">
                        <SelectValue placeholder={isRTL ? 'اختر الدور...' : 'Select role...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map(role => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Show when same role selected */}
                {compareRoleA && compareRoleB && compareRoleA === compareRoleB && (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertTitle className="text-amber-800">
                      {isRTL ? 'اختر أدوار مختلفة' : 'Select different roles'}
                    </AlertTitle>
                    <AlertDescription className="text-amber-700">
                      {isRTL 
                        ? 'الرجاء اختيار دورين مختلفين للمقارنة'
                        : 'Please select two different roles to compare'}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Comparison Results */}
                {roleComparison && (
                  <>
                    {/* Summary Counts */}
                    <div className="flex flex-wrap gap-3 p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-sm font-medium text-emerald-800">
                          {isRTL ? `فقط في ${roleComparison.roleAName}:` : `Only in ${roleComparison.roleAName}:`} {roleComparison.counts.onlyA}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 rounded-full">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span className="text-sm font-medium text-orange-800">
                          {isRTL ? `فقط في ${roleComparison.roleBName}:` : `Only in ${roleComparison.roleBName}:`} {roleComparison.counts.onlyB}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-200 rounded-full">
                        <div className="w-3 h-3 rounded-full bg-slate-500" />
                        <span className="text-sm font-medium text-slate-700">
                          {isRTL ? 'مشتركة:' : 'Shared:'} {roleComparison.counts.shared}
                        </span>
                      </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder={isRTL ? 'بحث في الصلاحيات...' : 'Search permissions...'}
                          value={compareSearch}
                          onChange={(e) => setCompareSearch(e.target.value)}
                          className="pl-10"
                          data-testid="compare-search"
                        />
                      </div>
                      <Select value={compareModuleFilter} onValueChange={setCompareModuleFilter}>
                        <SelectTrigger className="w-[180px]" data-testid="compare-module-filter">
                          <Filter className="h-4 w-4 mr-2" />
                          <SelectValue placeholder="Filter by module" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{isRTL ? 'جميع الوحدات' : 'All Modules'}</SelectItem>
                          {PERMISSION_MODULES.map(mod => (
                            <SelectItem key={mod} value={mod}>{mod.charAt(0).toUpperCase() + mod.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showDiffsOnly}
                          onChange={(e) => setShowDiffsOnly(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                          data-testid="compare-diffs-only"
                        />
                        <span className="text-sm text-slate-600">
                          {isRTL ? 'عرض الاختلافات فقط' : 'Show differences only'}
                        </span>
                      </label>
                    </div>

                    {/* Permissions Lists */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Only in Role A */}
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-emerald-100 px-4 py-3 flex items-center justify-between border-b border-emerald-200">
                          <span className="font-medium text-emerald-800">
                            {isRTL ? `فقط في ${roleComparison.roleAName}` : `Only in ${roleComparison.roleAName}`}
                          </span>
                          <Badge className="bg-emerald-500 text-white">
                            {roleComparison.counts.filteredOnlyA}
                          </Badge>
                        </div>
                        <div className="p-4 max-h-[300px] overflow-auto space-y-1">
                          {roleComparison.onlyInA.length > 0 ? (
                            roleComparison.onlyInA.map(perm => (
                              <div key={perm} className="flex items-center gap-2 py-1 px-2 bg-emerald-50 rounded text-sm">
                                <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                                <span className="font-mono text-xs text-emerald-800 break-all">{perm}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-400 text-sm italic">
                              {isRTL ? 'لا توجد صلاحيات حصرية' : 'No exclusive permissions'}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Only in Role B */}
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-orange-100 px-4 py-3 flex items-center justify-between border-b border-orange-200">
                          <span className="font-medium text-orange-800">
                            {isRTL ? `فقط في ${roleComparison.roleBName}` : `Only in ${roleComparison.roleBName}`}
                          </span>
                          <Badge className="bg-orange-500 text-white">
                            {roleComparison.counts.filteredOnlyB}
                          </Badge>
                        </div>
                        <div className="p-4 max-h-[300px] overflow-auto space-y-1">
                          {roleComparison.onlyInB.length > 0 ? (
                            roleComparison.onlyInB.map(perm => (
                              <div key={perm} className="flex items-center gap-2 py-1 px-2 bg-orange-50 rounded text-sm">
                                <CheckCircle2 className="h-3 w-3 text-orange-500 flex-shrink-0" />
                                <span className="font-mono text-xs text-orange-800 break-all">{perm}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-400 text-sm italic">
                              {isRTL ? 'لا توجد صلاحيات حصرية' : 'No exclusive permissions'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Shared Permissions (only when toggle is off) */}
                    {!showDiffsOnly && roleComparison.shared.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="bg-slate-100 px-4 py-3 flex items-center justify-between border-b">
                          <span className="font-medium text-slate-700">
                            {isRTL ? 'صلاحيات مشتركة' : 'Shared Permissions'}
                          </span>
                          <Badge variant="outline" className="border-slate-400 text-slate-600">
                            {roleComparison.counts.filteredShared}
                          </Badge>
                        </div>
                        <div className="p-4 max-h-[200px] overflow-auto">
                          <div className="flex flex-wrap gap-2">
                            {roleComparison.shared.map(perm => (
                              <Badge key={perm} variant="secondary" className="font-mono text-xs bg-slate-100 text-slate-700">
                                {perm}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Initial state - no roles selected */}
                {!compareRoleA && !compareRoleB && (
                  <div className="text-center py-12 text-slate-400">
                    <GitBranch className="h-12 w-12 mx-auto mb-4 opacity-50 rotate-90" />
                    <p>{isRTL ? 'اختر دورين للمقارنة' : 'Select two roles to compare'}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab C: RACI Processes */}
        <TabsContent value="raci" data-testid="raci-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'عمليات RACI' : 'RACI Processes'}</CardTitle>
              <CardDescription>
                {isRTL 
                  ? 'المسؤول، المُحاسب، المستشار، المُبلغ - لعمليات الإجازات والطلبات'
                  : 'Responsible, Accountable, Consulted, Informed - for Leave and Request workflows'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* HR Manager Accountability Note */}
              <Alert className="mb-4 bg-violet-50 border-violet-200">
                <Zap className="h-4 w-4 text-violet-600" />
                <AlertTitle className="text-violet-800">
                  {isRTL ? 'من يمكنه التجاوز' : 'Who can override an approval'}
                </AlertTitle>
                <AlertDescription className="text-violet-700">
                  {isRTL
                    ? 'مدير الموارد البشرية هو المسؤول عن العمليات أدناه، وهو الدور الوحيد في الموارد البشرية الذي يمكنه تجاوز مرحلة موافقة المدير — والمشرف العام كذلك. إداري الموارد البشرية يمتلك نفس الصلاحيات بدون هذا التجاوز، ومساعد الموارد البشرية لا يملكه. لا يُقبل أي تجاوز بدون سبب مكتوب (10 أحرف على الأقل)، ويُسجَّل كل تجاوز في سجل التدقيق مع اسم من نفّذه.'
                    : 'HR Manager is accountable for the processes below and is the only HR role that can override the manager\u2019s approval stage \u2014 Super Admin can too. HR Admin holds the same authority without that override, and HR Assistant does not have it. No override is accepted without a written reason of at least 10 characters, and every one is recorded in the audit log against the person who made it.'}
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                {/* RACI Legend */}
                <div className="flex flex-wrap gap-3 p-3 bg-slate-50 rounded-lg">
                  {Object.entries(RACI_COLORS).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2">
                      <Badge className={cn(val.bg, val.text, "text-xs font-bold")}>{key}</Badge>
                      <span className="text-sm text-slate-600">{val.label}</span>
                    </div>
                  ))}
                </div>

                {/* Search */}
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={isRTL ? 'بحث في العمليات...' : 'Search processes...'}
                    value={raciSearch}
                    onChange={(e) => setRaciSearch(e.target.value)}
                    className="pl-10"
                    data-testid="raci-search"
                  />
                </div>

                {/* RACI Table */}
                <div className="overflow-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-slate-700">{isRTL ? 'العملية' : 'Process'}</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">R</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">A</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">C</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">I</th>
                        <th className="px-4 py-3 text-center font-semibold text-slate-700">{isRTL ? 'تجاوز' : 'Bypass'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredRaci.map((process, idx) => (
                        <tr key={process.process_code} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{process.process_name_en}</div>
                            <div className="text-xs text-slate-500 font-mono">{process.process_code}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {process.responsible.map(r => (
                                <Badge key={r} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  {r.replace('hr_', '').replace('_', ' ')}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className="bg-purple-600 text-white text-xs">
                              {process.accountable.replace('hr_', '')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {process.consulted.length > 0 ? process.consulted.map(c => (
                                <Badge key={c} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  {c.replace('hr_', '')}
                                </Badge>
                              )) : <span className="text-slate-400">-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1 justify-center">
                              {process.informed.length > 0 ? process.informed.map(i => (
                                <Badge key={i} variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-300">
                                  {i.replace('hr_', '')}
                                </Badge>
                              )) : <span className="text-slate-400">-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {process.can_be_bypassed ? (
                              <Badge className="bg-violet-100 text-violet-700 text-xs">
                                <Zap className="h-3 w-3 mr-1" />
                                {isRTL ? 'نعم' : 'Yes'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-500 text-xs">
                                <Lock className="h-3 w-3 mr-1" />
                                {isRTL ? 'لا' : 'No'}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab D: My Effective Permissions */}
        <TabsContent value="my-permissions" data-testid="my-perms-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'صلاحياتي الفعّالة' : 'My Effective Permissions'}</CardTitle>
              <CardDescription>
                {isRTL ? 'الصلاحيات المخصصة لدورك الحالي' : 'Permissions granted to your current role'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {myPermissions && (
                <div className="space-y-6">
                  {/* User Role Info */}
                  <div className="flex flex-wrap gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">
                        {isRTL ? 'الدور:' : 'Role:'} 
                      </span>
                      <Badge className={cn(
                        ROLE_COLOR_MAP[myPermissions.role]?.bg || 'bg-slate-100',
                        ROLE_COLOR_MAP[myPermissions.role]?.text || 'text-slate-800'
                      )}>
                        {myPermissions.role_display_name}
                      </Badge>
                    </div>
                    {myPermissions.is_hr_staff && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                        <Shield className="h-3 w-3 mr-1" />
                        HR Staff
                      </Badge>
                    )}
                    {myPermissions.can_bypass && (
                      <Badge variant="secondary" className="bg-violet-100 text-violet-700">
                        <Zap className="h-3 w-3 mr-1" />
                        {isRTL ? 'صلاحية التجاوز' : 'Bypass Capable'}
                      </Badge>
                    )}
                    <div className="text-sm text-slate-600">
                      {isRTL ? 'عدد الصلاحيات:' : 'Total Permissions:'} {myPermissions.permission_count}
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder={isRTL ? 'بحث في صلاحياتي...' : 'Search my permissions...'}
                      value={myPermSearch}
                      onChange={(e) => setMyPermSearch(e.target.value)}
                      className="pl-10"
                      data-testid="my-perms-search"
                    />
                  </div>

                  {/* Permissions grouped by module */}
                  <div className="space-y-4">
                    {Object.entries(groupedMyPermissions).sort().map(([module, perms]) => (
                      <div key={module} className="border rounded-lg overflow-hidden">
                        <div className="bg-slate-100 px-4 py-2 flex items-center justify-between">
                          <span className="font-medium text-slate-700 capitalize">{module}</span>
                          <Badge variant="outline" className="text-xs">{perms.length}</Badge>
                        </div>
                        <div className="p-4 flex flex-wrap gap-2">
                          {perms.map(perm => (
                            <Badge 
                              key={perm} 
                              variant="secondary" 
                              className="text-xs font-mono bg-slate-100 text-slate-700"
                            >
                              {perm.replace(`${module}_`, '')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
